import {
  ApplicationConfig,
  provideZoneChangeDetection,
  provideAppInitializer,
  inject,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { appRoutes } from './app.routes';
import { reducers, metaReducers } from './store';
import { DomainsEffects } from './store/entities/domains/domains.effects';
import { UserEffects } from './store/entities/user/user.effects';
import { FeaturesEffects } from './store/entities/features/features.effects';
import { ProfileEffects } from './store/entities/profile/profile.effects';
import { DomainChangeCoordinatorEffects } from './store/coordinators/domain-change.coordinator.effects';
import { LinksDomainCoordinatorEffects } from './store/coordinators/links-domain.coordinator';
import { UserDomainsEffects } from './store/entities/user-domains/user-domains.effects';
import { TagsEffects } from './store/entities/tags/tags.effects';
import { AttributesEffects } from './store/entities/attributes/attributes.effects';
import { IpExclusionsEffects } from './store/entities/ip-exclusions/ip-exclusions.effects';
import { QRTemplatesEffects } from './store/entities/qr-templates/qr-templates.effects';
import { LinkTemplatesEffects } from './store/entities/link-templates/link-templates.effects';
import { PendingLinksEffects } from './store/entities/pending-links/pending-links.effects';
import { TrustedDestinationsEffects } from './store/entities/trusted-destinations/trusted-destinations.effects';
import { PowerPagesEffects } from './store/entities/power-pages/power-pages.effects';
import { ELinksEffects } from './store/entities/e-links/e-links.effects';
import {
  authInterceptor,
  authErrorInterceptor,
  headersInterceptor,
  domainInterceptor,
  csrfInterceptor,
  forbiddenInterceptor,
} from './core/interceptors';
import { AppInitService } from './core/services/app-init.service';

function initializeApp(): Promise<void> {
  const appInitService = inject(AppInitService);

  return appInitService.initialize(['domains', 'user', 'profile']);
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimations(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(appRoutes),
    provideHttpClient(
      withInterceptors([
        headersInterceptor,
        csrfInterceptor,
        authInterceptor,
        authErrorInterceptor,
        forbiddenInterceptor, // Handle 403 errors after auth errors
        domainInterceptor,
      ])
    ),
    provideStore(reducers, { metaReducers }),
    provideEffects([
      DomainsEffects,
      UserEffects,
      FeaturesEffects,
      ProfileEffects,
      DomainChangeCoordinatorEffects,
      LinksDomainCoordinatorEffects,
      UserDomainsEffects,
      TagsEffects,
      QRTemplatesEffects,
      LinkTemplatesEffects,
      PendingLinksEffects,
      AttributesEffects,
      TrustedDestinationsEffects,
      IpExclusionsEffects,
      PowerPagesEffects,
      ELinksEffects,
    ]),
    provideAppInitializer(initializeApp),
  ],
};
